/**
 * The dinosaur card: opened by clicking a dinosaur in the yard, laid over
 * the yard panel. Shows the showcase art, level, flavour and what it lays,
 * an Evolve button when a splice fits, and a name field so the player can
 * name the dinosaur. Levelling up is the shop row's job, not the card's. Closes on the x, Escape, or a click outside the card.
 */

import { DINOS, artSizeOf } from '../data/dinos';
import type { DinoDef } from '../data/dinos';
import { dinoEpsAt, dinoLevel, epsBreakdown } from '../game/economy';
import { BOSS_FIGHTS_LIVE, BOSS_MIN_LEVEL, DEINOSUCHUS } from '../data/bosses';
import { canFight, restLeftSeconds, titleOf } from '../game/boss';
import { formatRate } from '../game/numbers';
import { NAME_MAX_LENGTH } from '../game/state';
import type { GameState } from '../game/state';
import { dinoSpriteUrl } from './sprites';
import {
  isEvolved,
  speciesFlavor,
  speciesName,
  spriteIdFor,
  usableItemFor,
} from '../game/inventory';

export interface DinoCardView {
  open(id: string): void;
  close(): void;
  readonly openId: string | null;
  /** Refresh the numbers; cheap when closed. */
  update(state: GameState, boost: number): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export interface DinoCardHooks {
  /** The player pressed Evolve; the caller finds the item and confirms. */
  onEvolve(def: DinoDef): void;
  /**
   * The player named (or, with an empty name, un-named) a dinosaur. A name
   * already on a Museum plaque is refused;  is how that plaque spells
   * it, so the card can say which name rather than only that it is gone.
   */
  onRename(def: DinoDef, name: string): { ok: true } | { ok: false; taken: string };
  /** The player pressed Challenge on a Legendary dinosaur. */
  onChallenge(def: DinoDef): void;
}

export function createDinoCard(host: HTMLElement, hooks: DinoCardHooks): DinoCardView {
  const overlay = el('div', 'dino-card__overlay');
  overlay.hidden = true;
  const card = el('div', 'dino-card');
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'false');

  const close = el('button', 'dino-card__close', '×');
  close.type = 'button';
  close.setAttribute('aria-label', 'Close');

  const art = el('div', 'dino-card__art');
  const img = document.createElement('img');
  img.alt = '';
  art.append(img);

  const body = el('div', 'dino-card__body');
  // The heading is the pet name once given, with the species beneath; the
  // "name" button (or the heading itself) swaps it for an input (Enter or
  // blur keeps, Escape cancels).
  const heading = el('div', 'dino-card__heading');
  const name = el('h3', 'dino-card__name');
  const rename = el('button', 'dino-card__rename', 'name');
  rename.type = 'button';
  rename.setAttribute('aria-label', 'Name this dinosaur');
  // A form, so Enter (and a phone keyboard's Go) submits like the check does.
  const form = el('form', 'dino-card__form');
  form.hidden = true;
  const input = document.createElement('input');
  input.className = 'dino-card__input';
  input.type = 'text';
  input.name = 'name';
  input.autocomplete = 'off';
  input.maxLength = NAME_MAX_LENGTH;
  input.placeholder = 'Give it a name';
  const submit = el('button', 'dino-card__submit', '✓');
  submit.type = 'submit';
  submit.setAttribute('aria-label', 'Keep this name');
  form.append(input, submit);
  heading.append(name, rename, form);
  const species = el('p', 'dino-card__species');
  // Kept as history once evolved: "Evolved from Ankylosaurus".
  const lineage = el('p', 'dino-card__lineage');
  lineage.hidden = true;
  // Why a name would not take. Hidden until it happens, so the card is not
  // carrying an empty line around for a rule most players never meet.
  const retired = el('p', 'dino-card__retired');
  retired.hidden = true;
  const level = el('p', 'dino-card__level');
  const flavor = el('p', 'dino-card__flavor');
  const lays = el('p', 'dino-card__lays');
  // Shown only while a usable splice is held for this dinosaur.
  const evolve = el('button', 'dino-card__button dino-card__button--evolve', 'Evolve');
  evolve.type = 'button';
  evolve.hidden = true;
  // Boss fights: a Legendary dinosaur (level 100) can challenge a boss.
  const challenge = el('button', 'dino-card__button dino-card__button--fight');
  challenge.type = 'button';
  challenge.hidden = true;
  // The title a boss win gives, for the rest of the era.
  const title = el('p', 'dino-card__title');
  title.hidden = true;
  const buttons = el('div', 'dino-card__buttons');
  buttons.append(evolve);
  if (BOSS_FIGHTS_LIVE) buttons.append(challenge);
  body.append(heading, title, retired, species, lineage, level, flavor, lays, buttons);

  card.append(close, art, body);
  overlay.append(card);
  host.append(overlay);

  let current: DinoDef | null = null;
  let lastText = '';
  let petName = '';
  /** The species name to show; updated from state on every update(). */
  let currentSpecies = '';

  function showName(): void {
    const label = currentSpecies || current?.name || '';
    name.textContent = petName || label;
    species.textContent = petName ? label : '';
    species.hidden = !petName;
  }

  function startRename(): void {
    input.value = petName;
    retired.hidden = true;
    form.hidden = false;
    name.hidden = true;
    rename.hidden = true;
    input.focus();
    input.select();
  }

  function endRename(keep: boolean): void {
    if (form.hidden) return;
    // Any earlier refusal is answered now; a new one below shows it again.
    retired.hidden = true;
    form.hidden = true;
    name.hidden = false;
    rename.hidden = false;
    if (keep && current) {
      const wanted = input.value.trim().slice(0, NAME_MAX_LENGTH);
      const result = hooks.onRename(current, wanted);
      if (!result.ok) {
        // Stay in the form with the offending name still in it: the player is
        // one word away from a name that works, and closing would lose it.
        retired.textContent = `"${result.taken}" is on a plaque in the Museum, so it can't be used again.`;
        retired.hidden = false;
        form.hidden = false;
        name.hidden = true;
        rename.hidden = true;
        input.focus();
        input.select();
        return;
      }
      petName = wanted;
      showName();
    }
    rename.textContent = petName ? 'rename' : 'name';
    rename.focus();
  }

  rename.addEventListener('click', startRename);
  name.addEventListener('click', startRename);
  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    endRename(true);
  });
  input.addEventListener('keydown', (ev) => {
    // Escape here cancels the edit rather than closing the card.
    if (ev.key !== 'Escape') return;
    ev.stopPropagation();
    endRename(false);
  });
  input.addEventListener('blur', () => endRename(true));
  // Pressing the check must not blur the input first (that would end the
  // edit and hide the button before its click lands), so keep focus put.
  submit.addEventListener('pointerdown', (ev) => ev.preventDefault());

  function doClose(): void {
    endRename(false);
    current = null;
    overlay.hidden = true;
  }

  close.addEventListener('click', doClose);
  overlay.addEventListener('pointerdown', (ev) => {
    if (ev.target !== overlay) return;
    // Clicking away keeps a name being typed, as the input's blur would; the
    // card stays open only if that name was refused.
    endRename(true);
    if (form.hidden) doClose();
  });
  window.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && current) doClose();
  });
  evolve.addEventListener('click', () => {
    if (current) hooks.onEvolve(current);
  });
  challenge.addEventListener('click', () => {
    if (current) hooks.onChallenge(current);
  });

  return {
    get openId() {
      return current?.id ?? null;
    },
    open(id) {
      const def = DINOS.find((d) => d.id === id);
      if (!def) return;
      current = def;
      lastText = '';
      petName = '';
      retired.hidden = true;
      currentSpecies = def.name;
      showName();
      rename.textContent = 'name';
      flavor.textContent = def.flavor;
      img.src = dinoSpriteUrl(def.id, 'full');
      // The card is reused, and update() only rewrites the lineage when the
      // species changes. A base tier's species is what open() just set, so
      // without this it kept the last evolved card's "Evolved from ...".
      lineage.textContent = '';
      lineage.hidden = true;
      art.style.setProperty('--art', String(artSizeOf(def, 'full')));
      overlay.hidden = false;
      close.focus();
    },
    close: doClose,
    update(state, boost) {
      if (!current) return;
      const def = current;
      // The species, art and lineage follow an evolution.
      const evolvedNow = isEvolved(def, state);
      const nextSpecies = speciesName(def, state);
      if (nextSpecies !== currentSpecies) {
        currentSpecies = nextSpecies;
        showName();
        flavor.textContent = speciesFlavor(def, state);
        img.src = dinoSpriteUrl(spriteIdFor(def, state), 'full');
        lineage.textContent = evolvedNow ? `Evolved from ${def.name}` : '';
        lineage.hidden = !evolvedNow;
      }
      // The Evolve button appears when a splice that fits is in the inventory.
      const item = usableItemFor(def, state);
      const canEvolve = item !== null;
      if (canEvolve !== !evolve.hidden) evolve.hidden = !canEvolve;
      if (item) evolve.title = `Use your ${item.name} on this dinosaur`;

      // Challenge from level 100 (dev server only for now); while resting it says for how long.
      const lvlNow = dinoLevel(def, state);
      const now = Date.now();
      const legendary = BOSS_FIGHTS_LIVE && lvlNow >= BOSS_MIN_LEVEL;
      challenge.hidden = !legendary;
      if (legendary) {
        const ready = canFight(def.id, lvlNow, state, now).ok;
        const mins = Math.ceil(restLeftSeconds(state, def.id, now) / 60);
        const text = ready ? `Challenge ${DEINOSUCHUS.name}` : `Resting · ${mins} min`;
        if (challenge.textContent !== text) challenge.textContent = text;
        challenge.disabled = !ready;
      }
      const held = titleOf(state, def.id);
      const titleText = held ? `the ${held}` : '';
      if (title.textContent !== titleText) {
        title.textContent = titleText;
        title.hidden = !held;
      }

      const stored = state.names[def.id] ?? '';
      if (stored !== petName && form.hidden) {
        petName = stored;
        showName();
        rename.textContent = petName ? 'rename' : 'name';
      }
      const lvl = dinoLevel(def, state);
      // Unhatched: what level 1 would lay with every multiplier in force now.
      const total =
        lvl > 0 ? (epsBreakdown(state, boost)[def.id] ?? 0) : dinoEpsAt(def, 1, state, boost);
      // One string for the whole card; rebuild the DOM only when it changes.
      const key = `${lvl}|${total}`;
      if (key === lastText) return;
      lastText = key;
      level.textContent = lvl > 0 ? `Level ${lvl}` : 'Not hatched';
      lays.textContent =
        lvl > 0
          ? `Lays ${formatRate(total)} eggs/s`
          : `Lays ${formatRate(total)} eggs/s at level 1`;
    },
  };
}
